import * as T from '@dayetopia/library/types';
import * as Sentry from '@sentry/react';
import { useAccountStore } from '@components/dashboard/context/UserDashboardContext';
import useAuthFetch from '@components/useAuthFetch';
import { AppState, DEFAULT_KLAVIYO_LIST_ID } from '@constants';
import { setOnSessionStorage } from '@utils/sessionStorage';

export default function useCustomer(state: AppState) {
  const { firebaseAuth } = state;

  const userInfo = useAccountStore((accountState) => accountState.userInfo);
  const setUserInfo = useAccountStore((accountState) => accountState.setUserInfo);
  const purchases = useAccountStore((accountState) => accountState.purchases);
  const setPurchases = useAccountStore((accountState) => accountState.setPurchases);
  const setLoading = useAccountStore((accountState) => accountState.setLoading);

  const fetch = useAuthFetch(firebaseAuth);

  const getAccountOrder = async (orderId: string): Promise<T.Order> => {
    const url = `/orders/customer/${orderId}`;
    return fetch.get(url);
  };

  const getAccountOrders = async (): Promise<T.Order[]> => {
    const url = `/account/orders`;
    return fetch.get(url);
  };

  const getAccountSubscriptions = async (): Promise<T.Subscription[]> => {
    const url = `/account/subscriptions`;
    return fetch.get(url);
  };

  const getAccountDetails = async (): Promise<T.UserInfo> => {
    const url = `/account/details`;
    return fetch.get(url);
  };

  const getBillingInfo = async (): Promise<T.BillingInfo> => {
    const url = `/recurly/billing-info`;
    return fetch.get(url);
  };

  const getSavedBillingInfo = async (): Promise<T.PaymentMethod[]> => {
    const url = `/recurly/billing-infos`;
    return fetch.get(url);
  };

  const getMarketingStatus = async () => {
    const masterList = DEFAULT_KLAVIYO_LIST_ID;
    const url = `/klaviyo/${masterList}/check`;
    return fetch.post(url, {});
  };

  const getDetails = async () => {
    setLoading(true);
    try {
      const subscriptions = await getAccountSubscriptions();
      const orders = await getAccountOrders();
      const user = await getAccountDetails();
      const billing = await getBillingInfo();
      const marketingStatus = await getMarketingStatus();
      if (marketingStatus && marketingStatus?.data?.attributes?.properties?.$consent?.includes('email')) {
        if (user.marketingPreferences) {
          user.marketingPreferences.marketingEmail = true;
        } else {
          user.marketingPreferences = {
            marketingEmail: true
          };
        }
      }

      setOnSessionStorage('userPurchasesCount', purchases.orders?.length ?? 0);
      setPurchases({ ...purchases, subscriptions: subscriptions ?? [], orders: orders ?? [], billingInfo: billing, fetchedAccountData: true });
      setUserInfo({ ...userInfo, ...user });
    } catch (err) {
      Sentry.captureException(err, { tags: { failed: 'customer_getDetails' } });
    } finally {
      setLoading(false);
    }
  };

  const refreshOrders = async () => {
    setLoading(true);
    try {
      const orders = await getAccountOrders();
      setPurchases({ ...purchases, orders: orders ?? [] });
      setOnSessionStorage('userPurchasesCount', orders?.length ?? 0);
    } catch (err) {
      Sentry.captureException(err, { tags: { failed: 'customer_refreshOrders' } });
    } finally {
      setLoading(false);
    }
  };

  const refreshSubscriptions = async () => {
    setLoading(true);
    try {
      const subscriptions = await getAccountSubscriptions();
      setPurchases({ ...purchases, subscriptions: subscriptions ?? [] });
    } catch (err) {
      Sentry.captureException(err, { tags: { failed: 'customer_refreshSubscriptions' } });
    } finally {
      setLoading(false);
    }
  };

  const update = async (updateObject: { [key: string]: any }) => {
    try {
      if (!firebaseAuth) throw Error('Firebase auth undefined');
      const { currentUser } = firebaseAuth;
      if (!currentUser) return;
      const path = `/customers/${currentUser.uid}`;
      setUserInfo({ ...userInfo, ...updateObject });
      await fetch.put(path, updateObject);
    } catch (err) {
      Sentry.captureException(err, { tags: { failed: 'customer_update' } });
    }
  };

  return { update, getDetails, getSavedBillingInfo, getAccountOrder, refreshOrders, refreshSubscriptions };
}
